/**
 * Booking and client notes (Traci's doc, Oct 2026): "editing and deleting notes (permission: only editable or
 * deletable by person who made the note). (with "edited" on the edited note so all knows it was edited)".
 * The text before an edit or delete is kept in the Audit Log (note_edited / note_deleted).
 */

/** Only the person who wrote the note. Notes with no author (written by the system) are nobody's to change. */
export function canChangeNote(note, actorId) {
  return Boolean(note?.created_by && actorId && note.created_by === actorId)
}

/**
 * Who may delete a booking note: its author, and Admin or Talent on any booking note (Traci, 21 Oct 2026: when Nautica
 * swaps a talent off a booking, the old talent's rate note should go, so Billing is not confused). Editing a note stays
 * with its author.
 */
export const NOTE_DELETERS = ['admin', 'talent_coordinator']
export function canDeleteNote(note, actorId, roles = null, anyNote = false) {
  if (canChangeNote(note, actorId)) return true
  if (!anyNote || !note) return false
  const list = roles instanceof Set ? [...roles] : Array.isArray(roles) ? roles : []
  return list.some(r => NOTE_DELETERS.includes(r))
}

// updated_at is stamped when the row is written, so it sits a moment after created_at on a note nobody edited.
const EDIT_GAP_MS = 5000

/** True once the note was changed after it was written. */
export function wasEdited(note) {
  const created = Date.parse(note?.created_at)
  const updated = Date.parse(note?.updated_at)
  return Number.isFinite(created) && Number.isFinite(updated) && updated - created > EDIT_GAP_MS
}

/**
 * What the page gets for one note: the author's id stays on the server; `mine` says whether Edit shows, `can_delete`
 * whether Delete does (`deleters`: the viewer's roles, for booking notes).
 */
export function noteView(note, actorId, deleters = null) {
  return {
    id: note.id,
    note: note.note,
    created_by_name: note.created_by_name || null,
    created_at: note.created_at,
    updated_at: note.updated_at || null,
    edited: wasEdited(note),
    mine: canChangeNote(note, actorId),
    can_delete: canDeleteNote(note, actorId, deleters, Boolean(deleters)),
    // The storage path stays on the server; the page opens the file through a short-lived link
    attachment: note.attachment_path ? { name: note.attachment_name || 'Attachment', type: note.attachment_type || null, size: note.attachment_size || null } : null
  }
}

/**
 * Documents on booking notes (Traci's doc, Oct 2026: "Ability to upload docs to booking notes as attachments").
 * PDFs, images, Word and Excel files, CSV and text, up to 10 MB.
 */
export const NOTE_FILE_TYPES = [
  'application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/webp',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', 'text/plain'
]
export const NOTE_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt'
export const NOTE_FILE_MAX_BYTES = 10 * 1024 * 1024

/** Why this file can't be attached, or null. `file` is a multer file or a browser File. */
export function noteFileProblem(file) {
  if (!file) return 'Choose a file'
  const type = file.mimetype || file.type
  if (!NOTE_FILE_TYPES.includes(type)) return 'Attach a PDF, image, Word, Excel, CSV or text file'
  if (Number(file.size) > NOTE_FILE_MAX_BYTES) return 'The file is larger than 10 MB'
  return null
}

/** The edit or delete to make, or the refusal. `text` is null for a delete. */
export function planNoteChange({ note, actorId, text, remove = false, roles = null, anyNote = false }) {
  if (!note) return { ok: false, status: 404, message: 'Note not found' }
  if (remove) {
    if (!canDeleteNote(note, actorId, roles, anyNote)) return { ok: false, status: 403, message: anyNote ? 'Only the person who wrote a note, Admin or Talent can delete it' : 'Only the person who wrote a note can edit or delete it' }
    return { ok: true, action: 'delete', own: canChangeNote(note, actorId) }
  }
  if (!canChangeNote(note, actorId)) return { ok: false, status: 403, message: 'Only the person who wrote a note can edit or delete it' }
  const next = String(text || '').trim().slice(0, 4000)
  if (!next) return { ok: false, status: 400, message: 'A note cannot be empty. Delete it instead.' }
  if (next === note.note) return { ok: true, action: 'none' }
  return { ok: true, action: 'edit', text: next }
}
